// Components
import {
  MainDiv,
  Header,
  SectionTitle,
  CloseButton,
  CloseImg,
  ClinicalDataDiv,
  LegendsDiv,
  LegendDiv,
  LegendColor,
  SectionText,
  StatusDiv,
  StatusText,
  StatusImg,
  PlotsDiv,
  PlotDiv,
} from "./styles";
import RadarPlot from "../radarPlot";
// Assets
import CloseIco from "../../../../../../assets/icons/btn_close_gray.svg";
import WarningIco from "../../../../../../assets/icons/ico_warning_white.svg";

const DetailedClinicalScores = ({ clinicalScores, setShowDetailedClinicalScores }) => {
  const mapStudyInfo = {
    pre: {
      label: "Baseline",
      background: "rgba(247, 147, 30, 0.2)",
      border: "#F7931E",
    },
    END_OF_THERAPY: {
      label: "End of therapy",
      background: "rgba(38, 169, 182, 0.2)",
      border: "#26A9B6",
    },
    FOLLOW_UP: {
      label: "Follow up",
      background: "rgba(17, 85, 204, 0.2)",
      border: "#15C",
    },
  };

  const renderLegends = (key) => {
    const currentLegend = clinicalScores.find((clinicalScore) => clinicalScore.KEY === key);

    if (currentLegend?.STATUS && currentLegend?.STATUS !== "WAITING") {
      const { border, label } = mapStudyInfo[key];

      return (
        <LegendDiv>
          <LegendColor background={border} />
          <SectionText>{label}</SectionText>
          {currentLegend?.STATUS === "MISSING_DATA" && (
            <StatusDiv margin="0 20px" background="#F15A24">
              <StatusText>Missing data</StatusText>
              <StatusImg src={WarningIco} alt="warning-ico" />
            </StatusDiv>
          )}
        </LegendDiv>
      );
    }
  };

  const renderARATRadarPlot = () => {
    let datasets = [];
    const initialLabels = ["Grasp", "Grip", "Gross movement", "Pinch"];
    const studyOrder = ["Baseline", "End of therapy", "Follow up"];

    let clinicalScoresValues = [];

    for (const clinicalScore of clinicalScores) {
      if (clinicalScore?.STATUS && clinicalScore?.STATUS !== "WAITING") {
        const { label, background, border } = mapStudyInfo[clinicalScore.KEY];

        let data = [
          clinicalScore?.CLINICAL_SCORES?.ARAT?.Grasp ? clinicalScore.CLINICAL_SCORES.ARAT.Grasp : NaN,
          clinicalScore?.CLINICAL_SCORES?.ARAT?.Grip ? clinicalScore.CLINICAL_SCORES.ARAT.Grip : NaN,
          clinicalScore?.CLINICAL_SCORES?.ARAT?.GrossMovement ? clinicalScore.CLINICAL_SCORES.ARAT.GrossMovement : NaN,
          clinicalScore?.CLINICAL_SCORES?.ARAT?.Pinch ? clinicalScore.CLINICAL_SCORES.ARAT.Pinch : NaN,
        ];

        clinicalScoresValues.push({ key: label, data });

        datasets.push({
          label: label,
          data: data,
          backgroundColor: background,
          borderColor: border,
          borderWidth: 1,
          opacity: 0.2,
        });
      }
    }

    const clinicalScoresOrdered = clinicalScoresValues.sort((a, b) => studyOrder.indexOf(a.key) - studyOrder.indexOf(b.key));

    const filteredClinicalScores = clinicalScoresOrdered.length > 2 ? clinicalScoresOrdered.slice(-2) : clinicalScoresOrdered;

    const LabelsInfo = initialLabels.map((label, i) => {
      if (filteredClinicalScores.length < 2) {
        return {
          label,
          labelColor: "#000",
        };
      }

      const firstValue = filteredClinicalScores[0].data[i];
      const secondValue = filteredClinicalScores[1].data[i];

      if (isNaN(firstValue) || isNaN(secondValue)) {
        return {
          label,
          labelColor: "#000",
        };
      }

      if (firstValue < secondValue) {
        return {
          label: `${label} ▲`,
          labelColor: "#22B573",
        };
      }

      if (firstValue > secondValue) {
        return {
          label: `${label} ▼`,
          labelColor: "#D9434E",
        };
      }

      return {
        label,
        labelColor: "#000",
      };
    });

    const data = {
      labels: LabelsInfo.map((label) => label.label),
      datasets: datasets,
    };
    return <RadarPlot data={data} LabelColors={LabelsInfo.map((label) => label.labelColor)} maxScale={18} />;
  };

  const renderMoCaRadarPlot = () => {
    let datasets = [];
    const initialLabels = [
      "Visuospatial / Executive Function",
      "Naming",
      "Attention",
      "Language",
      "Abstraction",
      "Delayed Recall",
      "Orientation",
    ];
    const studyOrder = ["Baseline", "End of therapy", "Follow up"];

    let clinicalScoresValues = [];

    for (const clinicalScore of clinicalScores) {
      if (clinicalScore?.STATUS && clinicalScore?.STATUS !== "WAITING") {
        const { label, background, border } = mapStudyInfo[clinicalScore.KEY];

        let data = [
          clinicalScore?.CLINICAL_SCORES?.MoCA?.["Visuospatial/Executive"]
            ? clinicalScore.CLINICAL_SCORES.MoCA["Visuospatial/Executive"]
            : NaN,
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Naming ? clinicalScore.CLINICAL_SCORES.MoCA.Naming : NaN,
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Attention ? clinicalScore.CLINICAL_SCORES.MoCA.Attention : NaN,
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Language ? clinicalScore.CLINICAL_SCORES.MoCA.Language : NaN,
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Abstraction ? clinicalScore.CLINICAL_SCORES.MoCA.Abstraction : NaN,
          clinicalScore?.CLINICAL_SCORES?.MoCA?.DelayedRecall ? clinicalScore.CLINICAL_SCORES.MoCA.DelayedRecall : NaN,
          clinicalScore?.CLINICAL_SCORES?.MoCA?.Orientation ? clinicalScore.CLINICAL_SCORES.MoCA.Orientation : NaN,
        ];

        clinicalScoresValues.push({ key: label, data });

        datasets.push({
          label: label,
          data: data,
          backgroundColor: background,
          borderColor: border,
          borderWidth: 1,
        });
      }
    }

    const clinicalScoresOrdered = clinicalScoresValues.sort((a, b) => studyOrder.indexOf(a.key) - studyOrder.indexOf(b.key));

    const filteredClinicalScores = clinicalScoresOrdered.length > 2 ? clinicalScoresOrdered.slice(-2) : clinicalScoresOrdered;

    const LabelsInfo = initialLabels.map((label, i) => {
      if (filteredClinicalScores.length < 2) {
        return {
          label,
          labelColor: "#000",
        };
      }

      const firstValue = filteredClinicalScores[0].data[i];
      const secondValue = filteredClinicalScores[1].data[i];

      if (isNaN(firstValue) || isNaN(secondValue)) {
        return {
          label,
          labelColor: "#000",
        };
      }

      if (firstValue < secondValue) {
        return {
          label: `${label} ▲`,
          labelColor: "#22B573",
        };
      }

      if (firstValue > secondValue) {
        return {
          label: `${label} ▼`,
          labelColor: "#D9434E",
        };
      }

      return {
        label,
        labelColor: "#000",
      };
    });

    const data = {
      labels: LabelsInfo.map((label) => label.label),
      datasets: datasets,
    };
    return <RadarPlot data={data} LabelColors={LabelsInfo.map((label) => label.labelColor)} maxScale={6} />;
  };

  return (
    <MainDiv>
      <Header>
        <SectionTitle>Detailed clinical scores</SectionTitle>
        <CloseButton onClick={() => setShowDetailedClinicalScores(false)}>
          <CloseImg src={CloseIco} alt="close-ico" />
        </CloseButton>
      </Header>
      <ClinicalDataDiv>
        <LegendsDiv>
          {renderLegends("pre")}
          {renderLegends("END_OF_THERAPY")}
          {renderLegends("FOLLOW_UP")}
        </LegendsDiv>
        <PlotsDiv>
          <PlotDiv width="42%">
            <SectionTitle margin="0 0 60px 0">ARAT (Motor)</SectionTitle>
            {renderARATRadarPlot()}
          </PlotDiv>
          <PlotDiv width="57%">
            <SectionTitle>MoCA (Cognition)</SectionTitle>
            {renderMoCaRadarPlot()}
          </PlotDiv>
        </PlotsDiv>
      </ClinicalDataDiv>
    </MainDiv>
  );
};

export default DetailedClinicalScores;
